import ReportProperty from '@/Enums/Report/BeeswaxReportProperties.enum';
import type { IState } from '@/Interfaces/Slices/ReportingDashboard/Adswizz/State.interface';
import { adswizzDefaultTotals } from '@/constants/reportingDashboard/adswizz';
import { DEFAULT_LOADING_REQUEST_ID } from '@/constants/slice';
import { fulfilledReportCaseHandler } from '@/helpers/reportingDashboard/slice';
import { fulfilledCaseHandler, pendingCaseHandler, rejectedCaseHandler } from '@/store/asyncThunkHandlers';
import { createSlice } from '@reduxjs/toolkit';
import { boundaryMap, getCampaignReport, getDateReport, getZipReport, reportMap } from './adswizzThunks';

const initialState: IState = {
  totals: adswizzDefaultTotals,

  dateReport: [],
  dayOfWeekReport: [],
  deviceReport: [],
  channelReport: [],
  campaignReport: [],
  orderReport: [],
  advertiserReport: [],
  creativeReport: [],
  publisherNameReport: [],
  dayTimeReport: [],
  genreReport: [],
  dealsReport: [],

  dmaReport: [],
  stateReport: [],
  countyReport: [],
  zipReport: [],
  congressionalDistrictReport: [],

  zipBoundaries: null,
  stateBoundaries: null,
  countyBoundaries: null,
  congressionalDistrictBoundaries: null,
  dmaBoundaries: null,

  campaigns: [],

  dateReportRequestId: DEFAULT_LOADING_REQUEST_ID,
  deviceReportRequestId: DEFAULT_LOADING_REQUEST_ID,
  channelReportRequestId: DEFAULT_LOADING_REQUEST_ID,
  campaignReportRequestId: DEFAULT_LOADING_REQUEST_ID,
  orderReportRequestId: DEFAULT_LOADING_REQUEST_ID,
  advertiserReportRequestId: DEFAULT_LOADING_REQUEST_ID,
  creativeReportRequestId: DEFAULT_LOADING_REQUEST_ID,
  publisherNameReportRequestId: DEFAULT_LOADING_REQUEST_ID,
  dayTimeReportRequestId: DEFAULT_LOADING_REQUEST_ID,
  genreReportRequestId: DEFAULT_LOADING_REQUEST_ID,
  dealsReportRequestId: DEFAULT_LOADING_REQUEST_ID,
  dmaReportRequestId: DEFAULT_LOADING_REQUEST_ID,
  zipReportRequestId: DEFAULT_LOADING_REQUEST_ID,
  zipBoundariesRequestId: DEFAULT_LOADING_REQUEST_ID,
  stateBoundariesRequestId: DEFAULT_LOADING_REQUEST_ID,
  countyBoundariesRequestId: DEFAULT_LOADING_REQUEST_ID,
  congressionalDistrictBoundariesRequestId: DEFAULT_LOADING_REQUEST_ID,
  dmaBoundariesRequestId: DEFAULT_LOADING_REQUEST_ID,

  errorCardMessage: '',
};

export const reportingDashboardAdswizz = createSlice({
  name: 'reportingDashboardAdswizz',
  initialState,
  reducers: {
    resetAdswizzReportsLoadingState: state => {
      Object.keys(state).forEach(key => {
        if (key.endsWith('RequestId')) {
          (state[key as keyof IState] as string) = DEFAULT_LOADING_REQUEST_ID;
        }
      });
      state.errorCardMessage = '';
    },
  },
  extraReducers: builder => {
    // Standard report handlers - exclude campaign which has special handling
    reportMap.forEach(([key, thunk]) => {
      builder
        .addCase(thunk.pending, pendingCaseHandler(key, []))
        .addCase(thunk.rejected, rejectedCaseHandler(key, []))
        .addCase(thunk.fulfilled, fulfilledReportCaseHandler(key));
    });

    // Boundary handlers - use shared fulfilledCaseHandler (payload used directly, no .report extraction)
    boundaryMap.forEach(([key, thunk]) => {
      builder
        .addCase(thunk.pending, pendingCaseHandler(key, null))
        .addCase(thunk.rejected, rejectedCaseHandler(key, null))
        .addCase(thunk.fulfilled, fulfilledCaseHandler(key));
    });

    // Campaign report - special handling to populate campaigns filter
    builder
      .addCase(getCampaignReport.pending, pendingCaseHandler('campaignReport', []))
      .addCase(getCampaignReport.rejected, rejectedCaseHandler('campaignReport', []))
      .addCase(getCampaignReport.fulfilled, (state, action) => {
        if (state.campaignReportRequestId !== action.meta.requestId) {
          return;
        }
        const { report, filters } = action.payload;
        state.campaignReportRequestId = null;
        state.campaignReport = report;
        // Populate campaigns filter when no campaigns are selected
        if (filters?.campaigns?.length === 0) {
          state.campaigns = (report ?? [])
            .map(campaign => ({
              value: campaign[ReportProperty.CAMPAIGN_ID],
              label: campaign[ReportProperty.CAMPAIGN_NAME],
            }))
            .sort((a, b) => a.label?.localeCompare(b.label));
        }
      });

    // Totals report - special handling for error messages
    builder
      .addCase(getDateReport.pending, pendingCaseHandler('dateReport'))
      .addCase(getDateReport.rejected, (state, action) => {
        if (state.dateReportRequestId === action.meta.requestId) {
          state.errorCardMessage = 'There seems to be a momentary issue loading your data. Please retry.';
          state.dateReportRequestId = null;
        }
      })
      .addCase(getDateReport.fulfilled, (state, action) => {
        if (state.dateReportRequestId === action.meta.requestId) {
          const { report, dayOfWeekReport, channelReport, totals } = action.payload;

          state.totals = totals ?? adswizzDefaultTotals;
          state.dateReportRequestId = null;
          state.dateReport = report ?? [];
          state.channelReport = channelReport ?? [];
          state.dayOfWeekReport = dayOfWeekReport ?? [];
          if (!totals?.[ReportProperty.IMPRESSIONS]) {
            state.errorCardMessage = "We couldn't find any data. Please adjust your filters and try again.";
          } else {
            state.errorCardMessage = '';
          }
        }
      });

    // Zip geo report - aggregate reports off zips
    builder
      .addCase(getZipReport.pending, pendingCaseHandler('zipReport'))
      .addCase(getZipReport.rejected, rejectedCaseHandler('zipReport'))
      .addCase(getZipReport.fulfilled, (state, action) => {
        if (state.zipReportRequestId === action.meta.requestId) {
          const { report, congressionalDistrictReport, countyReport, stateReport } = action.payload;
          state.zipReportRequestId = null;

          state.zipReport = report ?? [];
          state.congressionalDistrictReport = congressionalDistrictReport ?? [];
          state.countyReport = countyReport ?? [];
          state.stateReport = stateReport ?? [];
        }
      });
  },
});

export const { resetAdswizzReportsLoadingState } = reportingDashboardAdswizz.actions;

export default reportingDashboardAdswizz.reducer;
